import { useState } from "react";

import {
  RemissionNote,
  REMISSION_NOTE_STATUS_APPROVED_BY_SET,
  REMISSION_NOTE_STATUS_CANCELLATION_FAILED,
  REMISSION_NOTE_STATUS_CANCELLATION_REQUESTED,
  REMISSION_NOTE_STATUS_CANCELLED,
  REMISSION_NOTE_STATUS_ERROR,
  REMISSION_NOTE_STATUS_RESEND_REQUESTED,
  REMISSION_NOTE_STATUS_SET_UNKNOWN_ERROR,
  REMISSION_NOTE_STATUS_UPLOADED_TO_SET,
  formatRemissionNoteNumber,
} from "../../models/remission-note";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCollapse,
  CTooltip,
} from "@coreui/react";
import { formatUTCDateTime } from "../../utils/dates";
import RemissionNoteCrudStatus from "./RemissionNoteCrudStatus";
import RemissionNoteCrudActions from "./RemissionNoteCrudActions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import DocumentPrintButton from "../documents/DocumentPrintButton";
import { REMISSION_NOTES_RETRIEVE } from "../../auth/permissions";

interface RemissionNoteCardProps {
  remissionNote: RemissionNote;
  onXMLClick: (inv: RemissionNote) => void;
  onPDFClick: (inv: RemissionNote) => void;
  onCancellationClick: (inv: RemissionNote) => void;
  onCancellationRetryClick: (inv: RemissionNote) => void;
  onCancellationRevertClick: (inv: RemissionNote) => void;
  onUpdateClick: (inv: RemissionNote) => void;
  onDeleteClick: (inv: RemissionNote) => void;
  onConsultByCdcClick: (inv: RemissionNote) => void;
  onResendClick: (inv: RemissionNote) => void;
  onSendEmailClick: (inv: RemissionNote) => void;
  onHistoryClick: (inv: RemissionNote) => void;
}

export function RemissionNoteCard({
  remissionNote,
  onXMLClick,
  onPDFClick,
  onCancellationClick,
  onCancellationRetryClick,
  onCancellationRevertClick,
  onUpdateClick,
  onDeleteClick,
  onConsultByCdcClick,
  onResendClick,
  onSendEmailClick,
  onHistoryClick,
}: RemissionNoteCardProps) {
  const [open, setOpen] = useState(true);

  const getStatusColor = (status?: string): string => {
    switch (status) {
      case REMISSION_NOTE_STATUS_UPLOADED_TO_SET:
        return "warning";
      case REMISSION_NOTE_STATUS_ERROR:
        return "danger";
      case REMISSION_NOTE_STATUS_SET_UNKNOWN_ERROR:
        return "danger";
      case REMISSION_NOTE_STATUS_CANCELLATION_FAILED:
        return "danger";
      case REMISSION_NOTE_STATUS_APPROVED_BY_SET:
        return "success";
      case REMISSION_NOTE_STATUS_CANCELLATION_REQUESTED:
        return "light";
      case REMISSION_NOTE_STATUS_CANCELLED:
        return "info";
      case REMISSION_NOTE_STATUS_RESEND_REQUESTED:
        return "warning";
      default:
        return "info";
    }
  };

  let xmlButton = null;
  let pdfButton = null;

  if (remissionNote.xmlPath !== undefined) {
    xmlButton = (
      <PermissionRequiredComponent permissionName={REMISSION_NOTES_RETRIEVE}>
        <CTooltip content="XML">
          <CButton
            className="text-white"
            color="info"
            onClick={() => {
              onXMLClick(remissionNote);
            }}
          >
            <i className="fa fa-code"></i>
          </CButton>
        </CTooltip>
      </PermissionRequiredComponent>
    );
  }

  if (remissionNote.pdfPath !== undefined) {
    pdfButton = (
      <PermissionRequiredComponent permissionName={REMISSION_NOTES_RETRIEVE}>
        <CTooltip content="PDF">
          <CButton
            className="text-white"
            color="danger"
            onClick={() => {
              onPDFClick(remissionNote);
            }}
          >
            <i className="fa fa-file-pdf"></i>
          </CButton>
        </CTooltip>
      </PermissionRequiredComponent>
    );
  }

  return (
    <CCard className="mb-2">
      <CCardHeader
        className="d-flex justify-content-between align-items-center cursor-pointer"
        onClick={() => setOpen(!open)}
        color={getStatusColor(remissionNote.status)}
      >
        <span>
          <strong>{formatRemissionNoteNumber(remissionNote)}</strong>
        </span>
        <span className="d-flex align-items-center gap-2">
          <strong>
            {formatUTCDateTime(new Date(remissionNote.remissionNoteDate!))}
          </strong>
          <span
            className="rounded-circle bg-white d-flex justify-content-center align-items-center ml-1"
            style={{ width: "22px", height: "22px" }}
          >
            <i
              className={`fa fa-chevron-${open ? "up" : "down"}`}
              style={{ color: "black", fontSize: "12px" }}
            />
          </span>
        </span>
      </CCardHeader>
      <CCollapse show={open}>
        <CCardBody>
          <p>
            <strong>Razón Social:</strong> {remissionNote.clientSocialName}
          </p>
          <p>
            <strong>RUC:</strong> {remissionNote.clientRuc}
          </p>
          <p>
            <strong>Timbrado Usado:</strong> {remissionNote.stampIdentifier}
          </p>

          <p>
            <strong>Estado:</strong>{" "}
            <RemissionNoteCrudStatus remissionNote={remissionNote} />
          </p>

          <div>
            <strong>Archivos:</strong>{" "}
            <td className="text-center">
              <CButtonGroup>
                {xmlButton}
                {pdfButton}
                {remissionNote.pdfPath && remissionNote.id !== undefined && <DocumentPrintButton kind="remission-note" documentId={remissionNote.id} />}
              </CButtonGroup>
            </td>
          </div>
          <div>
            <strong>Acciones disponibles:</strong>{" "}
            <RemissionNoteCrudActions
              remissionNote={remissionNote}
              onCancellationClick={onCancellationClick}
              onCancellationRetryClick={onCancellationRetryClick}
              onCancellationRevertClick={onCancellationRevertClick}
              onUpdateClick={onUpdateClick}
              onDeleteClick={onDeleteClick}
              onConsultByCdcClick={onConsultByCdcClick}
              onResendClick={onResendClick}
              onSendEmailClick={onSendEmailClick}
              onHistoryClick={onHistoryClick}
            />
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>
  );
}
